/**
 * Measurement utilities for SideoutIQ.
 *
 * DB canonical units: height in centimeters (integer), weight in kilograms (decimal).
 * All display and input conversion happens here — components never do math directly.
 */

export type UnitSystem = 'imperial' | 'metric';

// ─── Display formatters ────────────────────────────────────────────────────────

/**
 * Format a canonical height (cm) for display.
 * Returns '—' when heightCm is null/undefined.
 *
 * @example formatHeight(178, 'imperial') → "5'10\""
 * @example formatHeight(178, 'metric')   → "178 cm"
 */
export function formatHeight(
  heightCm: number | null | undefined,
  system: UnitSystem
): string {
  if (heightCm == null) return '—';

  if (system === 'metric') {
    return `${heightCm} cm`;
  }

  const totalInches = heightCm / 2.54;
  const ft = Math.floor(totalInches / 12);
  const ins = Math.round(totalInches % 12);
  // Handle edge case where rounding pushes inches to 12
  if (ins === 12) return `${ft + 1}'0"`;
  return `${ft}'${ins}"`;
}

/**
 * Format a canonical weight (kg) for display.
 * Returns '—' when weightKg is null/undefined.
 *
 * @example formatWeight(68.04, 'imperial') → "150 lbs"
 * @example formatWeight(68.04, 'metric')   → "68.0 kg"
 */
export function formatWeight(
  weightKg: number | null | undefined,
  system: UnitSystem
): string {
  if (weightKg == null) return '—';

  if (system === 'metric') {
    return `${weightKg.toFixed(1)} kg`;
  }

  const lbs = Math.round(weightKg * 2.20462);
  return `${lbs} lbs`;
}

// ─── Input parsers (form → canonical DB value) ────────────────────────────────

/**
 * Convert height form inputs to canonical centimeters for DB storage.
 * Returns null if the input is empty or unparseable.
 *
 * Imperial mode: pass feet + inches as separate strings.
 * Metric mode:   pass cm as a single string.
 */
export function parseHeightToCm(
  system: UnitSystem,
  opts: { feet?: string; inches?: string } | { cm?: string }
): number | null {
  if (system === 'metric') {
    const cmStr = (opts as { cm?: string }).cm ?? '';
    // Accept both "178" and "1,88" / "1.88" (European meter format)
    const normalized = cmStr.trim().replace(',', '.');
    const v = parseFloat(normalized);
    if (isNaN(v) || v <= 0) return null;
    // If value looks like meters (1.5 – 2.5), convert to cm
    if (v > 1 && v < 3) return Math.round(v * 100);
    return Math.round(v);
  }

  // Imperial
  const feetInches = opts as { feet?: string; inches?: string };
  const ft = parseInt(feetInches.feet ?? '0') || 0;
  const ins = parseFloat(feetInches.inches ?? '0') || 0;
  if (ft === 0 && ins === 0) return null;
  return Math.round(ft * 30.48 + ins * 2.54);
}

/**
 * Convert weight form input to canonical kilograms for DB storage.
 * Returns null if the input is empty or unparseable.
 */
export function parseWeightToKg(
  system: UnitSystem,
  value: string
): number | null {
  const v = parseFloat(value.trim().replace(',', '.'));
  if (isNaN(v) || v <= 0) return null;
  if (system === 'imperial') {
    return Math.round(v * 0.453592 * 100) / 100; // lbs → kg, 2dp
  }
  return Math.round(v * 100) / 100; // already kg
}

// ─── DB → form helpers (for populating edit form) ─────────────────────────────

/**
 * Split a canonical height (cm) into feet + inches strings for an imperial form.
 */
export function heightCmToImperialFields(
  heightCm: number | null | undefined
): { feet: string; inches: string } {
  if (heightCm == null) return { feet: '', inches: '' };
  const totalInches = heightCm / 2.54;
  const ft = Math.floor(totalInches / 12);
  const ins = Math.round(totalInches % 12);
  if (ins === 12) return { feet: String(ft + 1), inches: '0' };
  return { feet: String(ft), inches: String(ins) };
}

/**
 * Convert canonical weight (kg) to a lbs display string for an imperial form.
 */
export function weightKgToImperialField(
  weightKg: number | null | undefined
): string {
  if (weightKg == null) return '';
  return String(Math.round(weightKg * 2.20462));
}

/**
 * Convert canonical weight (kg) to a kg display string for a metric form.
 */
export function weightKgToMetricField(
  weightKg: number | null | undefined
): string {
  if (weightKg == null) return '';
  return weightKg.toFixed(1);
}
